import { z } from 'zod';
import { AuditMeta, DocId, IsoTimestamp } from '../common.js';
import { PROVIDER_SEARCH_CEILING } from '../sociale-kaart.js';
/**
 * Sociale Kaart provider record. Matches spec §6.2 data model.
 *
 * Firestore path: providers/{providerId}
 *
 * Providers are cross-tenant-readable by default — the Sociale Kaart is a
 * platform-wide directory, not a per-tenant resource. Admin writes go through
 * suppohrt_admin only.
 */
export const ProviderType = z.enum([
    'ggz_instelling',
    'vrijgevestigd',
    'groepspraktijk',
    'e_health',
    'overig',
]);
export const TargetAudience = z.enum([
    'volwassenen',
    'jeugd',
    'jongeren',
    'ouderen',
    'paren',
    'gezinnen',
    // Besluit 8 okt 2026: vrouwspecifieke praktijken (Bloompsychologie) waren
    // anders alleen via de notitie te vinden.
    'vrouwen',
]);
/**
 * Zorgniveau: basis-GGZ (kortdurend, minder zwaar), specialistische GGZ (vraagt
 * een DSM-classificatie) of topklinisch/tertiair (pas na eerdere behandeling,
 * bijv. ArQ, De Viersprong). Meerdere tegelijk kan: veel praktijken bieden
 * bGGZ én sGGZ. Los van `type`, dat zegt wie de praktijk is, niet welke zorg.
 * Besluit 8 okt 2026.
 */
export const CareLevel = z.enum(['basis_ggz', 'specialistische_ggz', 'tertiair']);
export const PractitionerGender = z.enum(['man', 'vrouw']);
// 'pending' = een door een POH voorgestelde praktijk die nog op goedkeuring van
// een SUPPOHRT-admin wacht. De zoekservice filtert standaard op status=='active',
// dus een pending-record verdwijnt vanzelf uit de POH-lijst/kaart en is alleen
// via ?status=pending (de admin-aanvragentab) op te vragen.
export const ProviderStatus = z.enum(['active', 'closed', 'unknown', 'pending']);
export const ProviderSource = z.enum(['suppohrt_manual', 'zorgkaart_nl', 'external', 'lvvp']);
/**
 * Wat voor locatie een provider heeft. Bestaat omdat `address` anders het enige
 * veld is waar "dit is geen fysieke praktijk" in past: dat leverde 265
 * handmatige records op met "Online", "platform", "verschillende locaties" en
 * "-" als adres, plus 48 LVVP-records met "Onbekend". Een placeholder in een
 * adresveld is niet te filteren, niet te geocoderen en niet te onderscheiden
 * van een echt adres — vandaar een expliciet veld.
 */
export const LocationType = z.enum(['fysiek', 'online', 'meerdere_locaties', 'onbekend']);
/**
 * Een echt NL-adres bevat een huisnummer of postcode. Puur op cijfers testen is
 * grof maar sluit exact de klasse buiten die we hier bestrijden: praktijknamen
 * ("Praktijk Broeksma"), kale plaatsnamen ("Utrecht"), stadsdelen ("Zuid") en
 * placeholders ("-", "platform"). Een straat zonder huisnummer is geen adres
 * waar een POH iemand naartoe kan sturen.
 */
const ADDRESS_HAS_NUMBER = /\d/;
/** Waarden die een adres of plaats voorwenden zonder er een te zijn. */
const PLACEHOLDERS = new Set([
    '-',
    '--',
    'onbekend',
    'unknown',
    'n.v.t.',
    'nvt',
    'geen',
    'online',
    'platform',
    'divers',
    'verschillend',
    'verschillende',
    'verschillende locaties',
    'meerdere locaties',
]);
function isPlaceholder(s) {
    return PLACEHOLDERS.has(s.trim().toLowerCase());
}
/**
 * LEES-schema: bewust soepel. Bestaande docs dateren van vóór `locationType` en
 * dragen nog placeholder-adressen. `ProviderSchema.safeParse` draait op élke
 * read en `providers.service` slaat een record bij een parse-fout stil over
 * (`continue`) — een strenge refine hier zou 353 praktijken uit de Sociale Kaart
 * laten verdwijnen zonder dat iemand het merkt. Streng valideren gebeurt op de
 * schrijfkant: zie `ProviderLocationInput`.
 */
export const ProviderLocation = z.object({
    /** Afwezig = nog niet geclassificeerd (pre-migratie). */
    locationType: LocationType.optional(),
    address: z.string().max(500).optional(),
    city: z.string().max(200).optional(),
    region: z.string().min(1).max(200),
    province: z.string().min(1).max(100),
    coordinates: z
        .object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
    })
        .optional(),
});
/**
 * Leidt uit een legacy-locatie (van vóór `locationType`) af wat het type had
 * moeten zijn, op basis van wat er feitelijk in prod staat: "Online"/"platform",
 * "verschillende locaties"/"meerdere locaties", "-"/"Onbekend", kale
 * plaatsnamen ("Utrecht") en praktijknamen ("Praktijk Broeksma").
 *
 * Eén implementatie, twee afnemers: het admin-formulier (toont de gok, de admin
 * bevestigt of corrigeert) en de migratie in fase 3. Die twee mogen niet uit
 * elkaar lopen — anders classificeert het scherm anders dan de batch en is het
 * resultaat afhankelijk van wie het record aanraakte.
 *
 * Bewust conservatief: alles wat niet overtuigend online of multi-locatie is,
 * wordt `onbekend`. Een verkeerd label is erger dan een eerlijk "weet ik niet".
 */
export function classifyLegacyLocation(loc) {
    const address = loc.address?.trim() ?? '';
    const city = loc.city?.trim() ?? '';
    const keepCity = city && !isPlaceholder(city) ? city : undefined;
    const a = address.toLowerCase();
    if (address && !isPlaceholder(address) && ADDRESS_HAS_NUMBER.test(address)) {
        return { locationType: 'fysiek', address, city: keepCity };
    }
    if (/^(online|platform|beeldbellen|e-?health)$/.test(a)) {
        return { locationType: 'online', city: keepCity };
    }
    if (/(verschillend|meerdere|divers)/.test(a)) {
        return { locationType: 'meerdere_locaties', city: keepCity };
    }
    return { locationType: 'onbekend', city: keepCity };
}
/**
 * SCHRIJF-schema: de poort voor de admin-routes (POST + PATCH). Alles wat een
 * mens invoert moet hier langs, zodat er nooit meer een placeholder in het
 * adresveld belandt.
 *
 * Let op wat hier NIET langskomt — die paden dekken we via classifyLegacyLocation
 * i.p.v. dit schema, omdat ze geen 400 kunnen teruggeven aan een mens:
 *   - apply-enrichment: valideert ApplyEnrichmentBody en schrijft via de service,
 *     die met het SOEPELE ProviderSchema parst. Gedekt in buildProviderPatch.
 *   - seed-providers.ts / enrich-all-providers.ts: schrijven langs de routes om.
 *   - de LVVP-crawler: schrijft in Python buiten Zod om (guard volgt in fase 2).
 */
export const ProviderLocationInput = ProviderLocation.required({
    locationType: true,
}).superRefine((loc, ctx) => {
    const address = loc.address?.trim() ?? '';
    const city = loc.city?.trim() ?? '';
    if (loc.locationType === 'fysiek') {
        if (!address || isPlaceholder(address) || !ADDRESS_HAS_NUMBER.test(address)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['address'],
                message: 'Een fysieke praktijk heeft een adres met huisnummer of postcode nodig. Geen vast adres? Kies locatietype "online", "meerdere_locaties" of "onbekend".',
            });
        }
        if (!city || isPlaceholder(city)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['city'],
                message: 'Een fysieke praktijk heeft een plaats nodig.',
            });
        }
        return;
    }
    // Niet-fysiek: leeg laten. Anders kruipt de placeholder alsnog terug het
    // adresveld in ("Online" bij locationType online is dubbelop en niet-adres).
    if (address) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['address'],
            message: `Locatietype "${loc.locationType}" mag geen adres hebben. Laat het adres leeg of kies "fysiek".`,
        });
    }
    if (city && isPlaceholder(city)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['city'],
            message: `"${city}" is geen plaats. Laat leeg of vul een echte plaatsnaam in.`,
        });
    }
});
export const ProviderContact = z.object({
    phone: z.string().max(50).optional(),
    email: z.string().email().optional(),
    website: z.string().url().optional(),
});
export const ProviderSchema = AuditMeta.extend({
    id: DocId,
    name: z.string().min(1).max(300),
    type: ProviderType,
    location: ProviderLocation,
    /**
     * Extra vestigingen voor multi-locatie-organisaties (Arkin, Parnassia, ...).
     * `location` blijft de primaire/HQ-locatie; deze array draagt de overige
     * vestigingen, elk met eigen `coordinates` zodat de Sociale Kaart één pin per
     * vestiging toont — het meest bruikbaar voor de POH-GGZ die naar de
     * dichtstbijzijnde locatie verwijst. Additief en optioneel: solo-praktijken
     * hebben dit veld niet en bestaande docs veranderen niet. Lees bewust soepel
     * (zelfde `ProviderLocation` als `location`); de schrijfkant valideert elk
     * element via `ProviderLocationInput`.
     */
    additionalLocations: z.array(ProviderLocation).optional(),
    specialisations: z.array(z.string().min(1).max(100)).default([]),
    therapyTypes: z.array(z.string().min(1).max(100)).default([]),
    /**
     * Lees-soepel: een onbekende doelgroep valt weg in plaats van de hele praktijk
     * (een record valt weg bij één parse-fout). Zonder dit maakt elke nieuwe waarde,
     * zoals 'vrouwen' (8 okt 2026), de vorige revisie tot een rollback-gevaar.
     * De schrijfroutes overschrijven dit met de strikte enum.
     */
    targetAudience: z.preprocess((v) => (Array.isArray(v) ? v.filter((a) => TargetAudience.safeParse(a).success) : v), z.array(TargetAudience).min(1)),
    contactInfo: ProviderContact,
    status: ProviderStatus,
    /**
     * Human-readable waiting-time string (e.g. "8-12 weken", "<1 week").
     * Deliberately unstructured — sources disagree on exact semantics.
     */
    waitingTime: z.string().max(100).nullable(),
    /**
     * Aanmeldstop / patiëntenstop — de praktijk neemt tijdelijk geen nieuwe
     * cliënten aan (vaak door het omzetplafond van de verzekeraar). Self-service,
     * net als een wachttijd: elke ingelogde POH mag de vlag zetten/wissen zodat de
     * kaart in één oogopslag laat zien wie vol zit, zonder dat iedereen apart de
     * website checkt of belt. `intakeStoppedReason` is optionele context
     * ("t/m maart", "alleen jeugd"); `intakeStoppedUpdatedAt` stempelt wanneer.
     */
    intakeStopped: z.boolean().optional(),
    intakeStoppedReason: z.string().max(200).optional(),
    intakeStoppedUpdatedAt: IsoTimestamp.optional(),
    /**
     * Einddatum van de stop ("na 1 januari weer open"). Bewust een apart,
     * gestructureerd veld i.p.v. de datum in `intakeStoppedReason` proppen: zo is
     * hij eenduidig te tonen (en later te filteren op "stopt binnenkort") zonder
     * vrije tekst te parsen. Kale datum `YYYY-MM-DD`, geen timestamp — het gaat om
     * een kalenderdag, niet om een tijdstip. Lees-soepel/additief, net als de
     * andere intake-velden.
     */
    intakeStoppedUntil: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /**
     * Verzekeraar-specifieke stop. `intakeStopped` is de hoofd-vlag; deze lijst
     * verfijnt hem: leeg/afwezig = volledige stop (blokkeert iedereen, bestaand
     * gedrag), niet-leeg = de stop geldt alléén voor deze verzekeraars (het
     * 1nP-geval: open, maar niet voor Achmea-verzekerden). Labels uit
     * CANONICAL_INSURERS; lees-soepel (`z.string()`, geen enum, geen lengte-/
     * aantalsgrens) zodat geen enkele opgeslagen waarde het hele record stil laat
     * wegvallen (een record valt weg bij één parse-fout). De strikte poort zit op
     * de schrijfroute (curated-set + max 40), net als bij `intakeStoppedUntil`.
     * Additief.
     */
    intakeStoppedInsurers: z.array(z.string()).optional(),
    /**
     * Leeg/afwezig = onbekend, niet "geen". Lees-soepel via .catch: een onbekende
     * waarde laat het veld vallen in plaats van de hele praktijk uit de kaart
     * (een record valt weg bij één parse-fout). De schrijfroutes zijn strikt.
     */
    careLevels: z.array(CareLevel).optional().catch(undefined),
    /** Toegankelijk zonder verwijzing van de huisarts (welzijn, Mee & de Wering). */
    noReferralNeeded: z.boolean().optional().catch(undefined),
    notes: z.string().max(2000).optional(),
    /**
     * When the provider data was last verified. Used for data-freshness UI.
     */
    lastVerifiedAt: IsoTimestamp,
    /**
     * Data-source indicator. Shown in admin UI + practitioner cards.
     */
    source: ProviderSource,
    /**
     * Deep-link to the source record (e.g. the LVVP member page). Rendered as
     * the "Bron"-chip link for provenance. Optional: hand-seeded records have none.
     */
    sourceUrl: z.string().url().optional(),
    /**
     * BIG-registratienummer. Durable merge key for the LVVP crawler (exact match
     * on later runs). Optional: hand-seeded records predate it and only get one
     * attached when the crawler fuzzy-matches them. Not the doc id.
     */
    bigNumber: z.string().max(50).optional(),
    /**
     * Languages the practitioner offers besides Dutch (LVVP "Taalbeheersing").
     * Backs the language filter.
     */
    languages: z.array(z.string().min(1).max(50)).optional(),
    practitionerGender: PractitionerGender.optional(),
    /** LVVP practical-access flags. Stored now; no filter UI until asked. */
    eveningHours: z.boolean().optional(),
    weekendHours: z.boolean().optional(),
    wheelchairAccessible: z.boolean().optional(),
    /**
     * Record-wide human-edit lock. When true the LVVP crawler skips this doc
     * entirely (never overwrites curated data, never recreates after delete).
     * ponytail: record-wide; split into per-field flags only if the crawler
     * needs to keep refreshing a locked card's website/waitingTime.
     */
    locked: z.boolean().optional(),
    /**
     * Set by the crawler when a fuzzy dedup match was too weak to auto-merge.
     * Surfaced as a filter in the beheer list instead of a separate review queue.
     */
    needsReview: z.boolean().optional(),
    /**
     * Set by the LVVP crawler when a member is no longer in LVVP's search results.
     * The crawler also flips `status` to 'unknown' (so the POH list hides it); the
     * admin keeps seeing it in Beheer with a "niet meer gevonden"-chip. Cleared
     * automatically when the member reappears — the crawler does a full `set` that
     * drops this flag and restores `status: 'active'`. Fase 2, decision 2026-07-23.
     */
    lvvpStale: z.boolean().optional(),
    /**
     * When the LVVP crawler last saw this member in the search results. Drives the
     * stale-sweep (records with a stamp older than the run-start weren't found this
     * run) and shows the admin how long a stale record has been gone.
     */
    lvvpLastSeenAt: IsoTimestamp.optional(),
});
/**
 * Input for the Bulk Excel import flow. Preserves optional fields' absence
 * so the import UI can surface missing data as validation warnings per row.
 */
export const ImportProviderRow = ProviderSchema.omit({
    id: true,
    createdAt: true,
    createdBy: true,
    updatedAt: true,
    updatedBy: true,
    lastVerifiedAt: true,
}).extend({
    /**
     * Optional — server assigns if missing at import time.
     */
    lastVerifiedAt: IsoTimestamp.optional(),
});
/**
 * Query parameters for the Sociale Kaart search endpoint.
 */
export const ProviderSearchQuery = z.object({
    province: z.string().max(100).optional(),
    region: z.string().max(200).optional(),
    /**
     * Multi-select. Express parses repeated `?specialisation=A&specialisation=B`
     * into an array; a single value arrives as a string. The preprocess step
     * normalises both to a string[] so the service always sees one shape.
     * Capped at 30 to stay inside Firestore's `array-contains-any` limit.
     */
    specialisation: z
        .preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), z.array(z.string().min(1).max(100)).max(30))
        .optional(),
    /**
     * Multi-select. Same wire shape as `specialisation`: repeated query
     * params ?targetAudience=volwassenen&targetAudience=jongeren get
     * preprocessed into a string[]. Capped at 10 (Firestore allows 30 for
     * array-contains-any; the enum only has 3 values today).
     */
    targetAudience: z
        .preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), z.array(TargetAudience).max(10))
        .optional(),
    /**
     * Multi-select language filter. Same wire shape as `specialisation`:
     * repeated ?language=Engels&language=Duits get preprocessed to a string[].
     */
    language: z
        .preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), z.array(z.string().min(1).max(50)).max(30))
        .optional(),
    type: ProviderType.optional(),
    /** Multi-select zorgniveau, zelfde wire-vorm als `specialisation`. */
    careLevel: z
        .preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), z.array(CareLevel).max(3))
        .optional(),
    status: ProviderStatus.optional(),
    /**
     * Data-source filter — "SUPPOHRT zelf" (suppohrt_manual) vs. de LVVP-ingest
     * (lvvp). Applied in-memory (see ProviderService.search) to avoid a new
     * composite index per source/filter combination.
     */
    source: ProviderSource.optional(),
    /**
     * Single-letter prefix filter on the provider name (A-Z letterbalk). Matched
     * case-insensitively against the first character. Applied in-memory. `#` is
     * the bucket for names not starting with A-Z (digits like "113"/"1nP").
     */
    letter: z
        .string()
        .length(1)
        .regex(/^[A-Za-z#]$/)
        .optional(),
    /** Sort direction on name. Default A-Z. */
    sort: z.enum(['asc', 'desc']).default('asc'),
    /**
     * Free-text search across name, specialisations, therapyTypes.
     */
    q: z.string().max(200).optional(),
    // Max == PROVIDER_SEARCH_CEILING: the map view and the "Alle" page size ask
    // for the whole filtered set in one call (no pagination on a map), so the
    // route must accept exactly what the service is willing to scan. Bind it to
    // the shared constant — a hard-coded copy drifted once already and made the
    // frontend's limit=3000 400 out while the list silently fell back to 25.
    limit: z.coerce.number().int().min(1).max(PROVIDER_SEARCH_CEILING).default(25),
    cursor: z.string().max(500).optional(),
});
//# sourceMappingURL=provider.js.map